import { Component, type ErrorInfo, type ReactNode } from 'react';

interface Props {
  children: ReactNode;
  /** What to show instead of the failed subtree. */
  fallback?: (reset: () => void) => ReactNode;
  /** Named in the log so a report says which part of the screen failed. */
  area: string;
}

interface State {
  error: Error | null;
}

/**
 * Contains a render failure to one part of the screen.
 *
 * Boundaries are placed around the conversation, the chat list and each route
 * rather than only at the root: a message that fails to render should cost the
 * user that message, not the whole application and everything they had typed
 * elsewhere.
 */
export class ErrorBoundary extends Component<Props, State> {
  override state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    // Deliberately no message content or identifiers: a crash report must not
    // become a channel for leaking what somebody was reading.
    console.error(`[${this.props.area}] Не удалось отрисовать интерфейс.`, {
      message: error.message,
      componentStack: info.componentStack,
    });
  }

  private readonly reset = (): void => {
    this.setState({ error: null });
  };

  override render(): ReactNode {
    if (!this.state.error) return this.props.children;
    if (this.props.fallback) return this.props.fallback(this.reset);
    return (
      <div className="state-block" role="alert">
        <strong>Что-то пошло не так</strong>
        <p>Эта часть интерфейса не отобразилась. Остальное приложение продолжает работать.</p>
        <button type="button" className="secondary-btn" onClick={this.reset}>
          Попробовать снова
        </button>
      </div>
    );
  }
}
